import React from "react";
import {
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";

export default function ClinicianTodoItem({ todo, onClick }) {
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "short",
      month: "short",
      day: "numeric",
    };
    const timeOptions = { hour: "numeric", minute: "numeric", hour12: true };
    return `${date.toLocaleDateString("en-US", options)}, ${date.toLocaleTimeString("en-US", timeOptions)}`;
  };

  const getTimeIndicator = (dateString) => {
    const appointmentDate = new Date(dateString);
    const now = new Date();
    const diffHours = (appointmentDate - now) / (1000 * 60 * 60);
    const diffDays = Math.abs(Math.floor(diffHours / 24));
    
    if (diffHours < 0) {
      return <span className="text-red-600 font-medium">{diffDays} {diffDays === 1 ? 'day' : 'days'} past due</span>;
    } else if (diffHours < 24) {
      return <span className="text-red-500 font-medium">Today</span>;
    } else if (diffHours < 48) {
      return <span className="text-amber-500 font-medium">Tomorrow</span>;
    } else {
      return <span className="text-blue-500">{diffDays} {diffDays === 1 ? 'day' : 'days'} away</span>;
    }
  };

  const getTodoIcon = () => {
    if (todo.type === "Treat") {
      return <ClipboardDocumentCheckIcon className="h-5 w-5 text-indigo-500" />;
    } else if (todo.type === "Soap") {
      return <DocumentTextIcon className="h-5 w-5 text-green-500" />;
    } else if (todo.type === "Approve SOAP") {
      return <ClipboardDocumentListIcon className="h-5 w-5 text-amber-500" />;
    }
  };

  const getTodoActionText = () => {
    if (todo.type === "Treat") {
      return "Start Treatment";
    } else if (todo.type === "Soap") {
      return "Complete SOAP";
    } else if (todo.type === "Approve SOAP") {
      return "Review SOAP";
    }
  };

  const getTodoActionButtonClass = () => {
    if (todo.type === "Treat") {
      return "text-indigo-700 bg-indigo-100 hover:bg-indigo-200";
    } else if (todo.type === "Soap") {
      return "text-green-700 bg-green-100 hover:bg-green-200";
    } else if (todo.type === "Approve SOAP") {
      return "text-amber-700 bg-amber-100 hover:bg-amber-200";
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick(todo);
    }
  };

  return (
    <div 
      onClick={handleClick}
      className={`rounded-sm shadow-sm border p-4 mb-3 hover:shadow-md transition-shadow duration-200 cursor-pointer ${
        todo.type === "Treat" ? "bg-indigo-50 border-indigo-100" :
        todo.type === "Soap" ? "bg-green-50 border-green-100" :
        todo.type === "Approve SOAP" ? "bg-amber-50 border-amber-100" :
        "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3 mt-1">
          {getTodoIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-medium truncate ${
              todo.type === "Treat" ? "text-indigo-900" :
              todo.type === "Soap" ? "text-green-900" :
              todo.type === "Approve SOAP" ? "text-amber-900" :
              "text-gray-900"
            }`}>
              {todo.client?.firstName} {todo.client?.lastName}
            </h3>
            <div className="ml-2 flex-shrink-0">
              {getTimeIndicator(todo.start)}
            </div>
          </div>
          
          <div className="mt-1 flex items-center text-sm text-gray-500">
            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
            <p>{formatDateTime(todo.start)}</p>
          </div>
          
          <div className="mt-1 text-sm text-gray-500">
            <p>{todo.service?.description}</p>
          </div>
          
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center">
              {todo.type === "Treat" && (
                <div className="flex items-center text-xs text-indigo-600">
                  <ClipboardDocumentCheckIcon className="h-4 w-4 mr-1" />
                  <span>Patient ready for treatment</span>
                </div>
              )}
              {todo.type === "Soap" && (
                <div className="flex items-center text-xs text-green-600">
                  <DocumentTextIcon className="h-4 w-4 mr-1" />
                  <span>SOAP notes pending</span>
                </div>
              )}
              {todo.type === "Approve SOAP" && (
                <div className="flex items-center text-xs text-amber-600">
                  <ClipboardDocumentListIcon className="h-4 w-4 mr-1" />
                  <span>SOAP notes need review</span>
                </div>
              )}
            </div>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClick(e);
              }}
              className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${getTodoActionButtonClass()}`}
            >
              {getTodoActionText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 